import { Box } from "@chakra-ui/react"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import SearchInput from "@pathwright/ui/src/components/form/form-text-input/SearchInput"
import useIntersectionLoader from "@pathwright/ui/src/components/hooks/useIntersectionLoader"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import View from "@pathwright/ui/src/components/ui/View"
import { media } from "@pathwright/ui/src/components/utils/styles"
import styled from "styled-components"
import { ShareDumb } from "../../../share/Share"
import { useMentorGroupListContext } from "./MentorGroupListContext"
import MentorGroupMemberListItem, {
  MentorGroupMemberListItemAdd
} from "./MentorGroupMemberListItem"

const StyledHeader = styled(View.Primary)`
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ListContainer = styled.div`
  position: relative;
  padding-top: 10px;
  width: 100%;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  ${media.min.phone`
    max-height: calc(100vh - 400px);
  `};
`

function MemberItemsList() {
  const {
    mentorGroup,
    groupSubscription,
    permissions,
    search,
    searchedMembers
  } = useMentorGroupListContext()

  return searchedMembers.map((member) => (
    <MentorGroupMemberListItem
      key={member.id}
      mentorGroup={mentorGroup}
      groupSubscription={groupSubscription}
      permissions={permissions}
      membership={member}
      search={search}
    />
  ))
}

// Load more members when when MemberList has been
// scrolled to the bottom.
const IntersectionLoader = ({ hasMore, loadingMore, loadMore }) => {
  const intersectionRef = useIntersectionLoader({
    hasMore,
    loadingMore,
    loadMore
  })

  return hasMore ? (
    <Box ref={intersectionRef} pos="relative" h="50px">
      <LoadingCircle />
    </Box>
  ) : null
}

function EmptyList() {
  const { mentorGroup, groupSubscription } = useMentorGroupListContext()
  const { t } = useTranslate()

  return mentorGroup.permissions.can_add_member ? (
    <ShareDumb
      context={{ type: "group", typeId: mentorGroup.id }}
      displayType="modal"
    >
      {({ renderTrigger }) =>
        renderTrigger((props) => (
          <BlankSlate
            icons={null}
            heading={t("No members here yet")}
            body={
              groupSubscription
                ? t(
                    "Add someone to your group to get access to {{ product }}, share progress, and more.",
                    {
                      product: groupSubscription.price.product.name
                    }
                  )
                : t("Add someone to your group to share progress and more.")
            }
            primaryAction={{
              ...props,
              onClick: props.onToggle,
              children: t("Add members")
            }}
            meta={
              mentorGroup.seat_limit != null
                ? // if we were to add a _plural translation, which number would it be based on?
                  t("{{ filled }}/{{ limit }} subscription seats filled", {
                    filled: mentorGroup.seats_filled,
                    limit: mentorGroup.seat_limit
                  })
                : null
            }
          />
        ))
      }
    </ShareDumb>
  ) : (
    <BlankSlate heading={t("mentor_group.member.5.blank_slate.heading")} />
  )
}

const MentorGroupMemberList = () => {
  const { t } = useTranslate()
  const {
    mentorGroup,
    permissions,
    searchedMembersQuery,
    searchedMembers,
    searchedMembersPaginator,
    search,
    debouncingSeach,
    setSearch
  } = useMentorGroupListContext()

  const loading = searchedMembersQuery.loading || debouncingSeach
  const error = searchedMembersQuery.error

  const showSearch =
    // hacky check for if loading after clearing search value (initial search value is null)
    (loading && typeof search === "") ||
    search ||
    debouncingSeach ||
    searchedMembers.length > 7

  const tPrefix = "mentor_group.members.list"

  if (error) {
    return (
      <BlankSlate
        icons={null}
        heading="An unexpected error occurred."
        body={(error && error.message) || error}
        primaryAction={{
          children: t(`${tPrefix}.refresh`),
          onClick: () => searchedMembersQuery.refetch()
        }}
      />
    )
  }

  const shouldShowAddMember =
    permissions.can_add_member && !!searchedMembers?.length // && !search

  return (
    <Box minH="300px">
      {showSearch && (
        <StyledHeader>
          <SearchInput
            value={search}
            onChange={(search) => setSearch(search)}
            onClear={() => setSearch("")}
            placeholder={`${t(`Search`)}...`}
            searching={loading}
          />
        </StyledHeader>
      )}
      <ListContainer>
        {!mentorGroup ? (
          <LoadingCircle center />
        ) : loading || searchedMembers.length ? (
          <>
            <MemberItemsList members={searchedMembers} />
            <IntersectionLoader
              hasMore={searchedMembersPaginator.hasMore}
              loadingMore={loading || searchedMembersPaginator.loadingMore}
              loadMore={searchedMembersPaginator.loadMore}
            />
          </>
        ) : (
          <EmptyList />
        )}
      </ListContainer>
      {shouldShowAddMember && (
        <MentorGroupMemberListItemAdd>Add member</MentorGroupMemberListItemAdd>
      )}
    </Box>
  )
}

export default MentorGroupMemberList
